import { FormList } from '@mantine/form';

import {
  AcquirerTypes,
  IBusinessAcquirerType,
} from '../enums/acquirer-types.enum';
import {
  BusinessBankAccountDepositTypes,
  BusinessBankAccountTypes,
} from '../enums/bank.enums';
import { BusinessFileNames } from '../enums/business-file-names';
import { BusinessModeOfPayments } from '../enums/business-mode-payments';
import { BusinessTypes, BusinessTypesCode } from '../enums/business-type';
import { Entity } from '../enums/entity.enums';
import { Gender } from '../enums/gender-enums';
import { MerchantOnboardingStatus } from '../enums/merchant-onboarding-status';
import { MidFileNames } from '../enums/mid-file-names.enum';
import { Currency } from '../enums/retrieve-transaction.enums';
import { SettlementFileNames } from '../enums/settlement-file-names';
import { ApiIncorporator, ApiSignatory } from './incorporator-signatory.types';
import {
  MdrFee,
  RetrievedIncorporator,
  RetrievedSignatory,
} from './onboard-merchant-request.types';

export interface UpdateMerchantOnboardingDataRequest {
  merchantName: string;
  legalName?: string;
  businessHandle?: string;
  tin: string;

  address: {
    office: {
      city: string;
      line1: string;
      buildingNameAndNumber?: string;
      state: string;
      postalCode: string;
      contactNumber: string;
      barangay: string;
    };
    registered?: {
      city: string;
      line1: string;
      buildingNameAndNumber?: string;
      state: string;
      postalCode: string;
      contactNumber: string;
      barangay: string;
    };
  };
  isBrickAndMortarStore: boolean;
  industry: string;
  serviceDescription: string;
  estimatedMonthlyGrossTransaction?: string;
  modeOfPaymentId: BusinessModeOfPayments[];
  aubPartnerId?: string;
  acquirerIds?: AcquirerTypes[];
  currencyId: Currency | null;
  websites?: {
    businessWebsiteUrl?: string;
    isBusinessWebsiteUnderDevelopment?: boolean;
    facebookUrl?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    tiktokUrl?: string;
  };

  incorporators?: ApiIncorporator[];
  signatories?: ApiSignatory[];

  representatives: {
    authorized: {
      firstName: string;
      middleName?: string;
      lastName: string;
      emailAddress: string;
      contactNumber: string;
      dateOfBirth: Date;
      tin?: string;
      sss?: string;
      position: string;
      mothersMaidenName?: string;
      gender?: Gender;
      aubNationalityCode?: string;
      aubDeviceId?: string;
      aubIdCode?: string;
      aubIdNumber?: string;
      aubRegionCode?: string;
    };
    escalation?: {
      firstName: string;
      middleName?: string;
      lastName: string;
      emailAddress: string;
      contactNumber: string;
      dateOfBirth: Date;
      tin: string;
      sss: string;
      position: string;
    };
  };
  banks: {
    accountDepositTypeId: BusinessBankAccountDepositTypes;
    accountTypeId?: BusinessBankAccountTypes;
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    currencyId?: Currency;
  }[];

  isWantPackworksWallet: boolean;
  isWantPayMayaWallet: boolean;
  isWantPayMayaBankAccount: boolean;

  files?: MerchantFileMetadata[];
  mdrFees?: MdrFee[];
}

export interface RetrieveMerchantsRequest {
  page: number;
  limit?: number;
  merchantName?: string;
  onboardingStatus?: MerchantOnboardingStatus;
  businessType?: BusinessTypesCode;
  isActive?: boolean;
}

export interface MerchantFileMetadata {
  id: string;
  name: string;
  fileType: BusinessFileNames;
}

export interface SettlementFileMetadata {
  id: string;
  name: string;
  fileType: SettlementFileNames;
}

export interface MidFileMetaData {
  id: string;
  name: string;
  fileType: MidFileNames;
}

export interface GeneralOnboardingInformation {
  businessTypeId: BusinessTypes;
  merchantName: string;
  legalName: string | null; //Make sure this is | null because legalName property is optional(legalName?) in api side there are other similar cases below
  businessHandle: string;
  tin: string;

  address: {
    office: {
      city: string;
      line1: string;
      buildingNameAndNumber?: string;
      state: string;
      postalCode: string;
      contactNumber: string;
      barangay: string;
    };
    registered?: {
      city: string;
      line1: string;
      buildingNameAndNumber?: string;
      state: string;
      postalCode: string;
      contactNumber: string;
      barangay: string;
    };
  };

  isBrickAndMortarStore: boolean;
  industry: string;
  serviceDescription: string;
  modeOfPaymentId: BusinessModeOfPayments[];
  currencyId: Currency | null;
  estimatedMonthlyGrossTransaction?: string;
  aubPartnerId?: string;
  acquirers?: IBusinessAcquirerType[];

  websites: {
    businessWebsiteUrl?: string;
    isBusinessWebsiteUnderDevelopment: boolean;
    facebookUrl?: string;
    twitterUrl?: string;
    instagramUrl?: string;
    tiktokUrl?: string;
  };

  dateCreated: Date;
  dateApproved: Date;
  dateDeclined: Date;
  datePending: Date;
  onboardingStatus: MerchantOnboardingStatus;
  onboardingReferenceNumber: string; // Will always be defined because merchant has already onboarded
}

export interface BusinessOnboardingInformation {
  representatives: {
    authorized: {
      firstName: string;
      middleName: string;
      lastName: string;
      emailAddress: string;
      contactNumber: string;
      dateOfBirth: Date;
      tin?: string;
      sss?: string;
      position: string;
      mothersMaidenName?: string;
      gender?: Gender;
      aubNationalityCode?: string;
      aubDeviceId?: string;
      aubIdCode?: string;
      aubIdNumber?: string;
      aubRegionCode?: string;
    };
    escalation?: {
      firstName: string;
      middleName: string;
      lastName: string;
      emailAddress: string;
      contactNumber: string;
      dateOfBirth: Date;
      tin: string;
      sss: string;
      position: string;
    };
  };

  banks: {
    accountDepositTypeId: BusinessBankAccountDepositTypes;
    accountTypeId?: BusinessBankAccountTypes;
    bankName: string | null;
    accountName: string | null;
    accountNumber: string | null;
    currencyId?: Currency;
  }[];

  isWantPackworksWallet: boolean;
  isWantPayMayaWallet: boolean;
  isWantPayMayaBankAccount: boolean;
}

export interface BaseOnboardingResponse
  extends GeneralOnboardingInformation,
    BusinessOnboardingInformation {
  signatories?: RetrievedSignatory[];
  incorporators?: RetrievedIncorporator[];

  onboardingStatus: MerchantOnboardingStatus;
  onboardingReferenceNumber: string; // Will always be defined because merchant has already onboarded

  declinedReason: string | null;
  isActive: boolean;
  disabledReason: string;

  files: MerchantFileMetadata[];
  mdrFees: MdrFee[];
  parentTypeId: number;
}

export interface OnboardingInformationByTab {
  general: GeneralOnboardingInformation;
  signatories?: RetrievedSignatory[];
  incorporators?: RetrievedIncorporator[];
  businessInformation: BusinessOnboardingInformation;
  documents: MerchantFileMetadata[];

  disabledReason?: string;
  isActive: boolean;

  declinedReason: string | null;
  accountId: number;
  mdrFees?: MdrFee[];
}

export interface RetrieveMerchantResponse extends BaseOnboardingResponse {
  merchantId: number;
}

export interface IPaginationMeta {
  /**
   * the amount of items on this specific page
   */
  itemCount: number;
  /**
   * the total amount of items
   */
  totalItems?: number;
  /**
   * the amount of items that were requested per page
   */
  itemsPerPage: number;
  /**
   * the total amount of pages in this paginator
   */
  totalPages?: number;
  /**
   * the current page this paginator "points" to
   */
  currentPage: number;
}

export interface BasicMerchant {
  merchantId: number;
  entityType: Entity;
  entityName: string;
  isActive: boolean;
  onboardingStatus: MerchantOnboardingStatus;
  onboardingReferenceNumber: string;
  merchantName: string;
  businessType: string;
  businessAddress: {
    city: string;
    line1: string;
    buildingNameAndNumber?: string;
    state: string;
    postalCode: string;
    contactNumber: string;
  };
  resellerName: string;
  dateCreated: Date;
  dateApproved: Date;
  dateDeclined: Date;
  datePending: Date;
  acquirersStatus: AcquirersStatus;
}

export interface AcquirersStatus {
  aub?: MerchantOnboardingStatus;
  ub?: MerchantOnboardingStatus;
  maya?: MerchantOnboardingStatus;
}

export interface RetrieveMerchantsResponse {
  items: BasicMerchant[];
  metadata: IPaginationMeta;
}

export enum MerchantOnboardingStatusAction {
  APPROVE = 'APPROVE',
  DECLINE = 'DECLINE',
}

export interface UpdateMerchantOnboardingStatusRequest {
  merchantIds: number[];
  onboardingStatus: MerchantOnboardingStatusAction;
  declinedReason?: string;
}

export interface UpdateFormType {
  legalName: string;
  city: string;
  line1: string;
  buildingNameAndNumber: string;
  state: string;
  postalCode: string;
  contactNumber: string;
  tin: string;
  isBrickAndMortarStore: boolean;
  industry: string;
  serviceDescription: string;
  businessHandle?: string;
  businessWebsiteUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  isBusinessWebsiteUnderDevelopment?: boolean;
  tiktokUrl?: string;
  twitterUrl?: string;
  aFirstName: string;
  aMiddleName?: string;
  aLastName: string;
  aEmailAddress: string;
  aContactNumber: string;
  aDateOfBirth: Date;
  aTin: string;
  aSss: string;
  eFirstName: string;
  eMiddleName?: string;
  eLastName: string;
  eEmailAddress: string;
  eContactNumber: string;
  eDateOfBirth: Date;
  eTin: string;
  eSss: string;
}

export interface UpdateFormTypeNew {
  businessTypeId: BusinessTypes;

  merchantName: string;
  merchantBusinessName: string;
  businessHandle?: string;
  merchantCompanyTin: string;

  mainOfficeContactNumber: string;
  mainOfficeBldgNameNo: string;
  mainOfficeStreetAddress: string;
  mainOfficeBarangay: string;
  mainOfficeCity: string;
  mainOfficeProvince: string;
  mainOfficePostalCode: string;

  registeredContactNumber: string;
  registeredBldgNameNo: string;
  registeredStreetAddress: string;
  registeredBarangay: string;
  registeredCity: string;
  registeredProvince: string;
  registeredPostalCode: string;

  isBrickAndMortarStore: boolean;
  industry: string;
  serviceDescription: string;
  modeOfPayment: BusinessModeOfPayments[];

  businessWebsiteUrl: string;
  facebookUrl: string;
  twitterUrl: string;
  instagramUrl: string;
  tiktokUrl: string;
  isBusinessWebsiteUnderDevelopment: boolean;

  incorporators: FormList<{
    firstName: string;
    middleName: string;
    hasMiddleName: boolean;
    lastName: string;
    bldgNameNo: string;
    emailAddress: string;
    contactNumber: string;
    birthDate?: Date | null;
    residentialAddress: string;
    barangay: string;
    city: string;
    province: string;
    postalCode: string;
  }>;

  signatories: FormList<{
    firstName: string;
    middleName: string;
    hasMiddleName: boolean;
    lastName: string;
    position: any;
  }>;
}
